import { ReactNode } from 'react';
import { When } from 'react-if';
import styled from 'styled-components';
import { CssFuncs } from '../../../../foundation/css/css.funcs';
import { TripleX } from '../../../../foundation/layout/triple-x';
import { Icon } from '../../icon/icon';

type VariantSet<T> = {
  base?: {
    normal?: T;
    hover?: T;
  };
  selected?: {
    normal?: T;
    hover?: T;
  };
  disabled?: {
    normal?: T;
    hover?: T;
  };
};

export type StyleConfig = {
  sizing: { shrinkX: string; shrinkY: string; borderRadius: string; gap: string };
  background?: { color: VariantSet<string> };
  icon?: { size: string; color: VariantSet<string> };
  text?: { size: string; weight: number; color: VariantSet<string> };
  config?: {
    componentId: string;
    displayName: string;
  };
};

const createStyle = (styleConfig: StyleConfig) => {
  const Container = styled.div.withConfig({
    componentId: styleConfig.config?.componentId + '_container',
    displayName: styleConfig.config?.displayName + '.Container',
  })`
    padding: ${styleConfig.sizing.shrinkY ?? '0px'} ${styleConfig.sizing.shrinkX ?? '0px'};
    border-radius: ${styleConfig.sizing.borderRadius ?? '4px'};

    background: ${styleConfig.background?.color?.base?.normal};
    &:hover {
      background: ${styleConfig.background?.color?.base?.hover};
    }

    &.selected {
      background: ${styleConfig.background?.color?.selected?.normal};
      &:hover {
        background: ${styleConfig.background?.color?.selected?.hover};
      }
    }

    transition: background 0.2s linear;
    user-select: none;
    cursor: pointer;
  `;

  const Text = styled.div.withConfig({
    componentId: styleConfig.config?.componentId + '_text',
    displayName: styleConfig.config?.displayName + '.Text',
  })`
    font-size: ${styleConfig.text?.size};
    font-weight: ${styleConfig.text?.weight};

    color: ${styleConfig.text?.color?.base?.normal};
    ${Container}:hover & {
      color: ${styleConfig.text?.color?.base?.hover};
    }

    ${Container}.selected & {
      color: ${styleConfig.text?.color?.selected?.normal};
    }
    ${Container}.selected:hover & {
      color: ${styleConfig.text?.color?.selected?.hover};
    }

    user-select: none;
  `;

  const Icon = styled.div.withConfig({
    componentId: styleConfig.config?.componentId + '_icon',
    displayName: styleConfig.config?.displayName + '.Icon',
  })`
    width: ${styleConfig.icon?.size};
    height: ${styleConfig.icon?.size};

    color: ${styleConfig.icon?.color?.base?.normal};
    ${Container}:hover & {
      color: ${styleConfig.icon?.color?.base?.hover};
    }

    ${Container}.selected & {
      color: ${styleConfig.icon?.color?.selected?.normal};
    }
    ${Container}.selected:hover & {
      color: ${styleConfig.icon?.color?.selected?.hover};
    }
  `;

  return { Container, Text, Icon };
};

type TripleButtonProps = {
  icon?: any;
  text?: string;
  right?: ReactNode;

  selected?: boolean;
  disabled?: boolean;

  onClick?: () => void;
};

export function createTripleButton(styleConfig: StyleConfig, options: { mouseUpMode?: boolean } = {}) {
  const Style = createStyle(styleConfig);
  return ({ icon, text, right, selected, disabled, onClick }: TripleButtonProps) => {
    return (
      <Style.Container
        onClick={
          options.mouseUpMode
            ? undefined
            : () => {
                if (disabled) {
                  return;
                }
                onClick?.();
              }
        }
        onMouseUp={
          options.mouseUpMode
            ? () => {
                if (disabled) {
                  return;
                }
                onClick?.();
              }
            : undefined
        }
        className={CssFuncs.className((names) => {
          names.push(selected ? 'selected' : '');
          names.push(disabled ? 'disabled' : '');
        })}
      >
        <TripleX.Container $gap={styleConfig.sizing.gap}>
          <When condition={icon}>
            <TripleX.Side>
              <Style.Icon>
                <Icon icon={icon} size={styleConfig.icon!.size} />
              </Style.Icon>
            </TripleX.Side>
          </When>
          <TripleX.Rest className="text-center">
            <Style.Text>{text ?? ' '}</Style.Text>
          </TripleX.Rest>
          <When condition={!!right}>
            <TripleX.Side>{right}</TripleX.Side>
          </When>
        </TripleX.Container>
      </Style.Container>
    );
  };
}
